import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { Card, SectionTitle } from '../../shared/components';
import { billingApi, Outstanding, OutstandingRow, rupees } from '../billing.api';
import BillDetailDialog from '../components/BillDetailDialog';
import { BillStatusChip } from '../components/BillStatusChip';
import useBillingFilters from '../hooks/useBillingFilters';

/**
 * What is owed, per client or per company, over bills (not proformas) for the year.
 *
 * Billed and Due leave cancelled bills out; their total shows under Cancelled. Paid is what
 * was received plus the TDS the client deducted. Due = Billed − Paid − Discount.
 */
export default function OutstandingPage() {
  const f = useBillingFilters();
  const [groupBy, setGroupBy] = useState<'CLIENT' | 'COMPANY'>('CLIENT');
  const [report, setReport] = useState<Outstanding | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [billId, setBillId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!f.ready) return;
    setLoading(true);
    try {
      setReport(await billingApi.outstanding({ financialYearId: f.financialYearId, companyId: f.companyId, groupBy }));
    } catch (e) {
      toast.error(logError({ message: 'Could not load the outstanding report', error: e, code: 'OUTSTANDING_FAILED' }));
    } finally {
      setLoading(false);
    }
  }, [f.ready, f.financialYearId, f.companyId, groupBy]);

  useEffect(() => {
    load();
  }, [load]);

  const key = (r: OutstandingRow) => `${groupBy}-${r.id ?? 'none'}`;
  const t = report?.total;

  return (
    <Stack gap={3}>
      <SectionTitle title='OUTSTANDING' />

      <Card sx={{ py: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
          <TextField select size='small' label='Financial year' value={f.financialYearId} sx={{ minWidth: 160 }}
            onChange={(e) => f.setFinancialYearId(e.target.value === '' ? '' : Number(e.target.value))}>
            <MenuItem value=''>All years</MenuItem>
            {f.years.map((y) => <MenuItem key={y.id} value={y.id}>{y.label}</MenuItem>)}
          </TextField>
          <TextField select size='small' label='Company' value={f.companyId} sx={{ minWidth: 220 }}
            onChange={(e) => f.setCompanyId(e.target.value === '' ? '' : Number(e.target.value))}>
            <MenuItem value=''>All companies</MenuItem>
            {f.companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
          </TextField>
          <ToggleButtonGroup size='small' exclusive value={groupBy} onChange={(_, v) => v && setGroupBy(v)}>
            <ToggleButton value='CLIENT'>By client</ToggleButton>
            <ToggleButton value='COMPANY'>By company</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {t && (
          <Stack direction='row' gap={4} flexWrap='wrap' sx={{ mb: 2 }}>
            <Tile label='Billed' value={rupees(t.billed)} />
            <Tile label='Paid' value={rupees(t.paid)} note={`${rupees(t.received)} received + ${rupees(t.tds)} TDS`} />
            <Tile label='Discount' value={rupees(t.discount)} />
            <Tile label='Cancelled' value={rupees(t.cancelled)} />
            <Tile label='Due' value={rupees(t.due)} strong />
          </Stack>
        )}
        {loading && <LinearProgress />}

        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox' />
              <TableCell>{groupBy === 'CLIENT' ? 'Client' : 'Company'}</TableCell>
              <TableCell align='right'>Bills</TableCell>
              <TableCell align='right'>Billed</TableCell>
              <TableCell align='right'>Received</TableCell>
              <TableCell align='right'>TDS</TableCell>
              <TableCell align='right'>Paid</TableCell>
              <TableCell align='right'>Discount</TableCell>
              <TableCell align='right'>Cancelled</TableCell>
              <TableCell align='right'>Due</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report?.rows.map((r) => (
              <Fragment key={key(r)}>
                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpen((o) => ({ ...o, [key(r)]: !o[key(r)] }))}>
                  <TableCell padding='checkbox'>
                    <IconButton size='small' aria-label='Show bills'>
                      <Icon icon={open[key(r)] ? 'mdi:chevron-up' : 'mdi:chevron-down'} />
                    </IconButton>
                  </TableCell>
                  <TableCell><b>{r.name}</b></TableCell>
                  <TableCell align='right'>{r.bills}</TableCell>
                  <Money v={r.billed} />
                  <Money v={r.received} />
                  <Money v={r.tds} />
                  <Money v={r.paid} />
                  <Money v={r.discount} />
                  <Money v={r.cancelled} />
                  <Money v={r.due} strong />
                </TableRow>
                <TableRow>
                  <TableCell {...({ colSpan: 10 } as object)} sx={{ py: 0, borderBottom: open[key(r)] ? undefined : 'none' }}>
                    <Collapse in={Boolean(open[key(r)])} unmountOnExit>
                      <Box sx={{ py: 1.5, pl: 6 }}>
                        <Table size='small'>
                          <TableHead>
                            <TableRow>
                              <TableCell>Bill no.</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell>{groupBy === 'CLIENT' ? 'Job' : 'Client'}</TableCell>
                              <TableCell align='right'>Total</TableCell>
                              <TableCell align='right'>Paid</TableCell>
                              <TableCell align='right'>Discount</TableCell>
                              <TableCell align='right'>Due</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {r.details.map((b) => (
                              <TableRow key={b.id} hover sx={{ cursor: 'pointer' }} onClick={() => setBillId(b.id)}>
                                <TableCell>{b.billNo}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(b.billDate).format('DD MMM YYYY')}</TableCell>
                                <TableCell>{groupBy === 'CLIENT' ? `${b.jobCode ?? ''} ${b.jobName ?? ''}` : b.clientName}</TableCell>
                                <Money v={b.totalAmount} />
                                <Money v={Number(b.received) + Number(b.tds)} />
                                <Money v={b.discountAmount} />
                                <Money v={b.due} />
                                <TableCell><BillStatusChip bill={b} /></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
            {!loading && report && report.rows.length === 0 && (
              <TableRow>
                <TableCell {...({ colSpan: 10 } as object)} align='center' sx={{ py: 4, color: 'text.secondary' }}>No bills for these filters.</TableCell>
              </TableRow>
            )}
            {t && report && report.rows.length > 0 && (
              <TableRow>
                <TableCell />
                <TableCell><b>Total</b></TableCell>
                <TableCell align='right'><b>{t.bills}</b></TableCell>
                <Money v={t.billed} strong />
                <Money v={t.received} strong />
                <Money v={t.tds} strong />
                <Money v={t.paid} strong />
                <Money v={t.discount} strong />
                <Money v={t.cancelled} strong />
                <Money v={t.due} strong />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <BillDetailDialog billId={billId} onClose={() => setBillId(null)} onChanged={load} onOpenBill={setBillId} />
    </Stack>
  );
}

function Money({ v, strong }: { v: number | string; strong?: boolean }) {
  return (
    <TableCell align='right' sx={{ whiteSpace: 'nowrap', fontWeight: strong ? 700 : undefined }}>
      {Number(v) ? rupees(v) : '—'}
    </TableCell>
  );
}

function Tile({ label, value, note, strong }: { label: string; value: string; note?: string; strong?: boolean }) {
  return (
    <Stack>
      <Typography variant='caption' color='text.secondary'>{label}</Typography>
      <Typography variant='h6' fontWeight={strong ? 700 : 500} color={strong ? 'error.main' : undefined}>{value}</Typography>
      {note && <Typography variant='caption' color='text.secondary'>{note}</Typography>}
    </Stack>
  );
}
